import { create } from 'zustand';
import type { AssetCategory, StoryCell, StoryElement, StoryboardProject, ViewMode } from '../types';
import { createCell, nowIso, normalizeZIndices, uid } from '../model/factories';

export type SaveStatus = 'saved' | 'saving' | 'unsaved' | 'error';

interface EditorState {
  project: StoryboardProject | null;
  activeCellId: string | null;
  selectedIds: string[];
  category: AssetCategory;
  viewMode: ViewMode;
  zoom: number;
  past: StoryboardProject[];
  future: StoryboardProject[];
  interactionStart: StoryboardProject | null;
  clipboard: StoryElement[];
  saveStatus: SaveStatus;
  exportOpen: boolean;
  revisionsOpen: boolean;
  presentationOpen: boolean;
  sidebarOpen: boolean;
  inspectorOpen: boolean;

  initialize: (project: StoryboardProject) => void;
  setProject: (project: StoryboardProject, resetHistory?: boolean) => void;
  mutate: (mutator: (draft: StoryboardProject) => void, recordHistory?: boolean) => void;
  startInteraction: () => void;
  finishInteraction: () => void;
  cancelInteraction: () => void;
  undo: () => void;
  redo: () => void;
  setActiveCell: (id: string) => void;
  setSelectedIds: (ids: string[]) => void;
  toggleSelected: (id: string) => void;
  setCategory: (category: AssetCategory) => void;
  setViewMode: (mode: ViewMode) => void;
  setZoom: (zoom: number) => void;
  addElement: (element: StoryElement) => void;
  deleteSelected: () => void;
  duplicateSelected: () => void;
  copySelected: () => void;
  pasteClipboard: () => void;
  moveLayer: (id: string, direction: 'front' | 'back' | 'forward' | 'backward') => void;
  addCell: () => void;
  duplicateCell: (id?: string) => void;
  deleteCell: (id?: string) => void;
  moveCell: (id: string, offset: -1 | 1) => void;
  setSaveStatus: (status: SaveStatus) => void;
  setExportOpen: (open: boolean) => void;
  setRevisionsOpen: (open: boolean) => void;
  setPresentationOpen: (open: boolean) => void;
  setSidebarOpen: (open: boolean) => void;
  setInspectorOpen: (open: boolean) => void;
}

const clone = <T,>(value: T): T => structuredClone(value);

const touch = (project: StoryboardProject): void => {
  project.updatedAt = nowIso();
};

const activeCell = (project: StoryboardProject, id: string | null): StoryCell | undefined =>
  project.cells.find((cell) => cell.id === id) ?? project.cells[0];

export const useEditorStore = create<EditorState>((set, get) => ({
  project: null,
  activeCellId: null,
  selectedIds: [],
  category: 'scenes',
  viewMode: 'cell',
  zoom: 1,
  past: [],
  future: [],
  interactionStart: null,
  clipboard: [],
  saveStatus: 'saved',
  exportOpen: false,
  revisionsOpen: false,
  presentationOpen: false,
  sidebarOpen: true,
  inspectorOpen: true,

  initialize: (project) =>
    set({
      project: clone(project),
      activeCellId: project.cells[0]?.id ?? null,
      selectedIds: [],
      viewMode: 'cell',
      zoom: 1,
      past: [],
      future: [],
      interactionStart: null,
      saveStatus: 'saved',
    }),

  setProject: (project, resetHistory = false) =>
    set((state) => ({
      project: clone(project),
      activeCellId: project.cells.some((cell) => cell.id === state.activeCellId)
        ? state.activeCellId
        : project.cells[0]?.id ?? null,
      selectedIds: [],
      past: resetHistory ? [] : state.past,
      future: resetHistory ? [] : state.future,
      interactionStart: null,
      saveStatus: 'unsaved',
    })),

  mutate: (mutator, recordHistory = true) => {
    const state = get();
    if (!state.project) return;
    const before = clone(state.project);
    const next = clone(state.project);
    mutator(next);
    touch(next);
    set({
      project: next,
      past: recordHistory ? [...state.past.slice(-99), before] : state.past,
      future: recordHistory ? [] : state.future,
      saveStatus: 'unsaved',
    });
  },

  startInteraction: () => {
    const state = get();
    if (state.project && !state.interactionStart) {
      set({ interactionStart: clone(state.project) });
    }
  },

  finishInteraction: () => {
    const state = get();
    if (!state.project || !state.interactionStart) return;
    const next = clone(state.project);
    touch(next);
    set({
      project: next,
      past: [...state.past.slice(-99), state.interactionStart],
      future: [],
      interactionStart: null,
      saveStatus: 'unsaved',
    });
  },

  cancelInteraction: () => {
    const state = get();
    if (!state.interactionStart) return;
    set({ project: state.interactionStart, interactionStart: null });
  },

  undo: () => {
    const state = get();
    if (!state.project || state.past.length === 0) return;
    const previous = state.past[state.past.length - 1];
    set({
      project: clone(previous),
      past: state.past.slice(0, -1),
      future: [clone(state.project), ...state.future].slice(0, 100),
      selectedIds: [],
      interactionStart: null,
      saveStatus: 'unsaved',
    });
  },

  redo: () => {
    const state = get();
    if (!state.project || state.future.length === 0) return;
    const next = state.future[0];
    set({
      project: clone(next),
      past: [...state.past.slice(-99), clone(state.project)],
      future: state.future.slice(1),
      selectedIds: [],
      interactionStart: null,
      saveStatus: 'unsaved',
    });
  },

  setActiveCell: (id) => set({ activeCellId: id, selectedIds: [], viewMode: 'cell' }),
  setSelectedIds: (ids) => set({ selectedIds: ids }),
  toggleSelected: (id) =>
    set((state) => ({
      selectedIds: state.selectedIds.includes(id)
        ? state.selectedIds.filter((item) => item !== id)
        : [...state.selectedIds, id],
    })),
  setCategory: (category) => set({ category, sidebarOpen: true }),
  setViewMode: (mode) => set({ viewMode: mode, selectedIds: [] }),
  setZoom: (zoom) => set({ zoom: Math.min(2.5, Math.max(0.35, zoom)) }),

  addElement: (element) => {
    const state = get();
    if (!state.project) return;
    let insertedId = element.id;
    state.mutate((draft) => {
      const cell = activeCell(draft, state.activeCellId);
      if (!cell) return;
      const maxZ = Math.max(-1, ...cell.elements.map((item) => item.zIndex));
      const inserted = { ...clone(element), id: element.id || uid('el'), zIndex: maxZ + 1 };
      insertedId = inserted.id;
      cell.elements.push(inserted);
    });
    set({ selectedIds: [insertedId] });
  },

  deleteSelected: () => {
    const state = get();
    if (!state.project || state.selectedIds.length === 0) return;
    state.mutate((draft) => {
      const cell = activeCell(draft, state.activeCellId);
      if (!cell) return;
      cell.elements = normalizeZIndices(cell.elements.filter((element) => !state.selectedIds.includes(element.id) || element.locked));
    });
    set({ selectedIds: [] });
  },

  duplicateSelected: () => {
    const state = get();
    if (!state.project || state.selectedIds.length === 0) return;
    const newIds: string[] = [];
    state.mutate((draft) => {
      const cell = activeCell(draft, state.activeCellId);
      if (!cell) return;
      const maxZ = Math.max(-1, ...cell.elements.map((item) => item.zIndex));
      const source = cell.elements.filter((element) => state.selectedIds.includes(element.id));
      source.forEach((element, index) => {
        const copy = clone(element);
        copy.id = uid('el');
        copy.name = `${element.name} copy`;
        copy.x += 30;
        copy.y += 30;
        copy.zIndex = maxZ + index + 1;
        newIds.push(copy.id);
        cell.elements.push(copy);
      });
    });
    set({ selectedIds: newIds });
  },

  copySelected: () => {
    const state = get();
    if (!state.project || state.selectedIds.length === 0) return;
    const cell = activeCell(state.project, state.activeCellId);
    set({ clipboard: clone(cell?.elements.filter((item) => state.selectedIds.includes(item.id)) ?? []) });
  },

  pasteClipboard: () => {
    const state = get();
    if (!state.project || state.clipboard.length === 0) return;
    const newIds: string[] = [];
    state.mutate((draft) => {
      const cell = activeCell(draft, state.activeCellId);
      if (!cell) return;
      const maxZ = Math.max(-1, ...cell.elements.map((item) => item.zIndex));
      state.clipboard.forEach((element, index) => {
        const copy = clone(element);
        copy.id = uid('el');
        copy.x += 40;
        copy.y += 40;
        copy.zIndex = maxZ + index + 1;
        newIds.push(copy.id);
        cell.elements.push(copy);
      });
    });
    set({ selectedIds: newIds });
  },

  moveLayer: (id, direction) => {
    const state = get();
    if (!state.project) return;
    state.mutate((draft) => {
      const cell = activeCell(draft, state.activeCellId);
      if (!cell) return;
      const sorted = [...cell.elements].sort((a, b) => a.zIndex - b.zIndex);
      const index = sorted.findIndex((element) => element.id === id);
      if (index < 0) return;
      const [item] = sorted.splice(index, 1);
      let target = index;
      if (direction === 'front') target = sorted.length;
      if (direction === 'back') target = 0;
      if (direction === 'forward') target = Math.min(sorted.length, index + 1);
      if (direction === 'backward') target = Math.max(0, index - 1);
      sorted.splice(target, 0, item);
      cell.elements = sorted.map((element, zIndex) => ({ ...element, zIndex }));
    });
  },

  addCell: () => {
    const state = get();
    if (!state.project || state.project.cells.length >= 100) return;
    let id = '';
    state.mutate((draft) => {
      const index = draft.cells.length;
      const cell = createCell(index, draft.cells[index - 1]?.scene.assetId ?? 'studio-loft');
      id = cell.id;
      draft.cells.push(cell);
    });
    set({ activeCellId: id, selectedIds: [], viewMode: 'cell' });
  },

  duplicateCell: (id) => {
    const state = get();
    if (!state.project || state.project.cells.length >= 100) return;
    const sourceId = id ?? state.activeCellId;
    let newId = '';
    state.mutate((draft) => {
      const index = draft.cells.findIndex((cell) => cell.id === sourceId);
      if (index < 0) return;
      const copy = clone(draft.cells[index]);
      copy.id = uid('cell');
      newId = copy.id;
      copy.title = `${copy.title} copy`;
      copy.audioDataUrl = undefined;
      copy.elements = copy.elements.map((element) => ({ ...element, id: uid('el') }));
      draft.cells.splice(index + 1, 0, copy);
    });
    if (newId) set({ activeCellId: newId, selectedIds: [], viewMode: 'cell' });
  },

  deleteCell: (id) => {
    const state = get();
    if (!state.project || state.project.cells.length <= 1) return;
    const targetId = id ?? state.activeCellId;
    let nextId: string | null = null;
    state.mutate((draft) => {
      const index = draft.cells.findIndex((cell) => cell.id === targetId);
      if (index < 0) return;
      draft.cells.splice(index, 1);
      nextId = draft.cells[Math.min(index, draft.cells.length - 1)]?.id ?? null;
    });
    set({ activeCellId: nextId, selectedIds: [] });
  },

  moveCell: (id, offset) => {
    const state = get();
    if (!state.project) return;
    state.mutate((draft) => {
      const index = draft.cells.findIndex((cell) => cell.id === id);
      const target = index + offset;
      if (index < 0 || target < 0 || target >= draft.cells.length) return;
      const [cell] = draft.cells.splice(index, 1);
      draft.cells.splice(target, 0, cell);
    });
  },

  setSaveStatus: (saveStatus) => set({ saveStatus }),
  setExportOpen: (exportOpen) => set({ exportOpen }),
  setRevisionsOpen: (revisionsOpen) => set({ revisionsOpen }),
  setPresentationOpen: (presentationOpen) => set({ presentationOpen }),
  setSidebarOpen: (sidebarOpen) => set({ sidebarOpen }),
  setInspectorOpen: (inspectorOpen) => set({ inspectorOpen }),
}));
